import React from 'react';
import type { Address } from '../types';

interface Props {
  value: Address;
  onChange: (next: Address) => void;
  prefix?: string; // for accessibility when two AddressFields appear on one form
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV',
  'WI','WY','DC',
];

const AddressFields: React.FC<Props> = ({ value, onChange, prefix = 'addr' }) => {
  const set = <K extends keyof Address>(key: K, v: Address[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
      <div className="sm:col-span-6">
        <label className="block text-xs text-text-light" htmlFor={`${prefix}-line1`}>Address</label>
        <input
          id={`${prefix}-line1`}
          required
          value={value.line1}
          onChange={e => set('line1', e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
      </div>
      <div className="sm:col-span-6">
        <label className="block text-xs text-text-light" htmlFor={`${prefix}-line2`}>Apt / Suite (optional)</label>
        <input
          id={`${prefix}-line2`}
          value={value.line2 ?? ''}
          onChange={e => set('line2', e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
      </div>
      <div className="sm:col-span-3">
        <label className="block text-xs text-text-light" htmlFor={`${prefix}-city`}>City</label>
        <input
          id={`${prefix}-city`}
          required
          value={value.city}
          onChange={e => set('city', e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
      </div>
      <div className="sm:col-span-1">
        <label className="block text-xs text-text-light" htmlFor={`${prefix}-state`}>State</label>
        <select
          id={`${prefix}-state`}
          required
          value={value.state}
          onChange={e => set('state', e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
        >
          <option value="">—</option>
          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs text-text-light" htmlFor={`${prefix}-zip`}>ZIP</label>
        <input
          id={`${prefix}-zip`}
          required
          inputMode="numeric"
          pattern="[0-9]{5}(-[0-9]{4})?"
          value={value.postal_code}
          onChange={e => set('postal_code', e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
      </div>
    </div>
  );
};

export default AddressFields;
